import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  effect,
  inject,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslocoDirective } from '@jsverse/transloco';
import { BehaviorSubject, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AuthStore } from 'src/core/stores';

@Component({
  selector: 'core-forgot-password',
  standalone: true,
  imports: [FormsModule, CommonModule, TranslocoDirective, ReactiveFormsModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './forgot-password.component.html',
})
export class ForgotPasswordComponent {
  private authStore = inject(AuthStore);
  private activatedRoute = inject(ActivatedRoute);
  private router = inject(Router);
  private cdr = inject(ChangeDetectorRef);

  emailResent = false;
  resetPasswordMailExpired$$ = new BehaviorSubject(false);
  genericError$$ = new BehaviorSubject(false);
  forgotPasswordForm!: FormGroup;
  showAlert = false;
  redirectUrl: string =
    this.activatedRoute.snapshot.queryParams['redirect'] || '/';

  constructor() {
    effect(() => {
      if (this.authStore.isAuthenticated()) {
        this.router.navigateByUrl(this.redirectUrl);
      }
    });

    const navigation = this.router.currentNavigation();
    if (
      navigation?.extras.state &&
      navigation?.extras.state['isResetPasswordRedirect']
    ) {
      this.resetPasswordMailExpired$$.next(true);
    }
    this.forgotPasswordForm = this.initFormValidation();
  }

  private initFormValidation(): FormGroup {
    return new FormGroup({
      forgotPasswordFormEmail: new FormControl('', {
        validators: [Validators.required, Validators.email],
      }),
    });
  }

  get forgotPasswordFormEmail() {
    return this.forgotPasswordForm.get('forgotPasswordFormEmail');
  }

  sendResetLink() {
    const email = this.forgotPasswordFormEmail?.value || undefined;
    this.authStore.setIsLoading(true);
    this.emailResent = true;

    this.authStore
      .sendPasswordResetMail(email)
      .pipe(
        catchError((err) => {
          console.error('Error occurred in sendResetLink:', err);
          this.authStore.setIsLoading(false);
          this.emailResent = false;
          this.showAlert = true;
          this.genericError$$.next(true);
          this.cdr.detectChanges();
          return of(); // return observable to continue stream
        }),
      )
      .subscribe({
        next: (result) => {
          this.emailResent = result;
          this.authStore.setIsLoading(false);
          this.cdr.detectChanges();
        },
      });
  }

  goHome() {
    this.router.navigate(['/']);
  }
}
